import type { ReactNode } from "react";

export const SITE_URL = "https://webnaprenajom.sk";
export const SITE_NAME = "Web na prenájom";
export const SITE_LOGO = `${SITE_URL}/favicon.png`;
export const ORG_SAME_AS = [
  "https://www.facebook.com/salelogics",
  "https://www.instagram.com/salelogics",
  "https://www.salelogics.sk",
];

export function absoluteUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: SITE_LOGO,
    image: SITE_LOGO,
    sameAs: ORG_SAME_AS,
    description:
      "Profesionálne webstránky na mesačný prenájom pre firmy a živnostníkov — dizajn, hosting, SSL, AI nástroje a technická podpora v cene.",
  };
}

export function buildWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "sk-SK",
  };
}

export function buildServiceSchema(opts: {
  name: string;
  description: string;
  url: string;
  priceFrom: number;
  areaServed?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    url: absoluteUrl(opts.url),
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: opts.areaServed ?? "SK",
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      price: opts.priceFrom,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: opts.priceFrom,
        priceCurrency: "EUR",
        unitText: "MONTH",
      },
    },
  };
}

/**
 * Only plain-string FAQ answers are eligible - JSX answers (inline links etc.)
 * are skipped so the schema never claims text that isn't literally what's
 * rendered on the page.
 */
export function buildFaqPageSchema(faqs: { q: string; a: ReactNode }[]) {
  const eligible = faqs.filter((f): f is { q: string; a: string } => typeof f.a === "string");
  if (eligible.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: eligible.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

export function buildArticleSchema(opts: {
  title: string;
  description: string;
  path: string;
  image: string;
  datePublished: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: opts.title,
    description: opts.description,
    image: absoluteUrl(opts.image),
    url: absoluteUrl(opts.path),
    datePublished: opts.datePublished,
    author: { "@id": `${SITE_URL}/#organization` },
    publisher: { "@id": `${SITE_URL}/#organization` },
    mainEntityOfPage: absoluteUrl(opts.path),
  };
}

export function buildItemListSchema(items: { name: string; url: string; position: number }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((item) => ({
      "@type": "ListItem",
      position: item.position,
      name: item.name,
      url: absoluteUrl(item.url),
    })),
  };
}

export function buildBreadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
